import { ReactNode, useMemo } from 'react';

import { ApplicationConfig } from '@repo/sdk/models/components';

import { SUPPORT_CONTACT_NUMBER } from '@/constants';
import { AgentProxyMockUser, getAgentProxyMockUser } from '@/utils/agent-proxy';

import { AuthContext, AuthContextValue } from './types';

interface AgentProxyAuthContextProviderProps {
  children: ReactNode;
  config: ApplicationConfig;
}

/**
 * Auth context provider for AgentProxy mode.
 * Provides a mock authenticated user without Auth0.
 *
 * In AgentProxy mode:
 * - User is always "authenticated" with a mock identity
 * - No tokens are used (requests go directly to local services)
 * - View-only mode is not supported
 * - Login/logout have no effect (logout just reloads the page)
 */
export const AgentProxyAuthContextProvider = ({
  children,
  config,
}: AgentProxyAuthContextProviderProps) => {
  const mockUser = getAgentProxyMockUser(config);

  // Defensive check: AgentProxy mode requires valid mock user configuration
  if (!mockUser) {
    console.error('AgentProxy mode enabled but mock user not configured');
    return (
      <div>{`AgentProxy configuration error. Please contact support at ${SUPPORT_CONTACT_NUMBER}.`}</div>
    );
  }

  return (
    <AgentProxyAuthContextProviderInner mockUser={mockUser}>
      {children}
    </AgentProxyAuthContextProviderInner>
  );
};

/**
 * Inner component with guaranteed mockUser.
 * Separates the conditional render from the hooks to follow React rules.
 */
const AgentProxyAuthContextProviderInner = ({
  children,
  mockUser,
}: {
  children: ReactNode;
  mockUser: AgentProxyMockUser;
}) => {
  const contextValue: AuthContextValue = useMemo(
    () => ({
      isLoading: false,
      isAuthenticated: true, // Always "authenticated" in AgentProxy mode
      isViewOnly: false, // AgentProxy users have full access
      error: undefined,
      user: mockUser,
      getAccessToken: async () => {
        // No token in AgentProxy mode - requests go directly to local services
        return '';
      },
      requestViewOnly: () => {
        console.warn('View-only mode not supported in AgentProxy mode');
      },
      requestActive: () => {
        console.warn('View-only mode not supported in AgentProxy mode');
      },
      login: () => {
        console.warn('Login not supported in AgentProxy mode');
      },
      logout: () => {
        // In AgentProxy mode, logout just reloads the page
        window.location.reload();
      },
    }),
    [mockUser]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
